import type { ScopeSourceDefinition, SingleEntityStore } from '@ts-scope/interfaces'
import type { ScopeSource } from '@ts-scope/types'
import type { ScopeStatus } from '@utils/stores/scoped-entry'

import { useAiSummarizeStore } from '@stores/ai-summarize'
import { useAssessmentResultStore } from '@stores/assessment-result'
import { useAssessmentResultsStore } from '@stores/assessment-results'
import { useBookingStore } from '@stores/booking'
import { useDisciplineStore } from '@stores/discipline'
import { useMasteryMapDashboardStore } from '@stores/mastery-map-dashboard'
import { useProgressReportStore } from '@stores/progress-report'
import { useScheduleStore } from '@stores/schedule'

import { useBookingsApi } from '@api/bookings/index.hook'
import { useCurrentBookingApi } from '@api/bookings/current.hook'
import { useFinishedBookingsApi } from '@api/bookings/finished.hook'
import { useActiveDisciplineInfoApi } from '@api/disciplines/active-discipline-info.hook'
import { useProgressReportsListApi } from '@api/disciplines/progress-reports/list.hook'
import { useAssessmentResultsListApi } from '@api/assessment-results/list.hook'
import { useMasteryMapDashboardApi } from '@api/mastery-map/mastery-map-dashboard.hook'
import { useAssessmentResultApi } from '@api/assessment-results/show.hook'
import { useTutorAvailability } from '@api/schedule/tutor-availability.hook'
import { useAiSummaryLoad } from '@hooks/ai-summarize/ai-summary-load.hook'
import { useAssessmentResultLoad } from '@hooks/assessments-result/assessment-result-load.hook'

const activeEntityStatus = (store: SingleEntityStore, id: number | null): ScopeStatus =>
  store.activeId === id ? store.getStatus : 'idle'

export const DEFAULT_SCOPE_REQUIRES: ScopeSource[] = ['disciplineInfo']

export const SCOPE_SOURCES: Record<ScopeSource, ScopeSourceDefinition> = {
  disciplineInfo: {
    param: 'discipline',
    keepOnRevisit: true,
    status: (id) => useDisciplineStore().getInfoStatus(id),
    start: (id, mode) => {
      void useActiveDisciplineInfoApi({ disciplineId: id, mode })
    },
  },
  currentBooking: {
    param: 'discipline',
    status: (id) => useBookingStore().getCurrentStatus(id),
    start: (id, mode) => {
      void useCurrentBookingApi({ disciplineId: id, background: mode === 'refresh' })
    },
  },
  bookings: {
    param: 'discipline',
    status: (id) => useBookingStore().getListStatus(id),
    start: (id, mode) => {
      void useBookingsApi({ disciplineId: id, background: mode === 'refresh' })
    },
  },
  finishedBookings: {
    param: 'discipline',
    status: (id) => useBookingStore().getFinishedStatus(id),
    start: (id, mode) => {
      void useFinishedBookingsApi({ disciplineId: id, background: mode === 'refresh' })
    },
  },
  progressReports: {
    param: 'discipline',
    status: (id) => useProgressReportStore().getListStatus(id),
    start: (id, mode) => {
      void useProgressReportsListApi().getProgressReportList(id, mode)
    },
  },
  tutorAvailability: {
    param: 'discipline',
    status: (id) => useScheduleStore().getAvailabilityStatus(id),
    start: (id, mode) => {
      void useTutorAvailability(id, mode)
    },
  },
  aiSummary: {
    param: 'summarize',
    status: (id) => activeEntityStatus(useAiSummarizeStore(), id),
    start: (id) => {
      void useAiSummaryLoad().load(id)
    },
  },
  masteryDashboard: {
    param: 'discipline',
    status: (id) => useMasteryMapDashboardStore().getStatus(id),
    start: (id, mode) => {
      void useMasteryMapDashboardApi().fetchMasteryDashboard(id, mode)
    },
  },
  assessmentResult: {
    param: 'assessment',
    status: (id) => activeEntityStatus(useAssessmentResultStore(), id),
    start: (id) => {
      void useAssessmentResultLoad().load(id)
    },
  },
  assessmentResults: {
    param: 'discipline',
    keepOnRevisit: true,
    status: (id) => useAssessmentResultsStore().getListStatus(id),
    start: (id, mode) => {
      void useAssessmentResultsListApi().fetchAssessmentResults(id, mode)
    },
  },
  assessmentResultsDocument: {
    param: 'assessment',
    status: (id) => useAssessmentResultsStore().getResultStatus(id),
    start: (id, mode) => {
      void useAssessmentResultApi().fetchAssessmentResult(id, mode)
    },
  },
}
